import { ref } from 'vue';
import { InventoryCondition, IParentBusiness } from '../domains/entities/Business';

/**
 * テーブルの種類を定義（スタイルクラス名の生成に使用）
 */
export type TableType = 'virtualized' | 'ez';

/**
 * テーブル状態管理のComposableの戻り値型
 */
export interface UseTableStateReturn {
  // スタイル関連
  tableTextStyleClass: (business: IParentBusiness, currentOpenId?: string, tableType?: TableType) => string;
  tableNumberStyleClass: (business: IParentBusiness, currentOpenId?: string, tableType?: TableType) => string;
  getNowInventoryStatusClass: (business: IParentBusiness, tableType?: TableType) => string;
  isNegative: (value?: number) => boolean;

  // イベントハンドラー
  handleToggle: (businessId: string, currentOpenId?: string) => string | undefined;
  createScrollHandler: (onScrollEnd?: () => void) => (event: Event) => void;
}

/**
 * テーブル状態管理のComposable
 * @returns テーブル状態管理に関連する関数群
 */
export const useTableState = (): UseTableStateReturn => {
  // 内部状態
  const scrollTimer = ref<number | null>(null);

  /**
   * テキストセルのスタイルクラスを返す
   * @param business - ビジネスオブジェクト
   * @param currentOpenId - 現在開いているビジネスID
   * @param tableType - テーブルの種類（デフォルト: 'virtualized'）
   * @returns CSSクラス名
   */
  const tableTextStyleClass = (business: IParentBusiness, currentOpenId?: string, tableType: TableType = 'virtualized'): string => {
    const isOpen = currentOpenId === business.businessId;

    if (tableType === 'ez') {
      return isOpen ? 'inventory-control__table-item-font-weight-bold' : 'inventory-control__table-item';
    }

    // 仮想テーブル用
    return isOpen ? 'table-cell table-cell--bold' : 'table-cell';
  };

  /**
   * 数値セルのスタイルクラスを返す
   * @param business - ビジネスオブジェクト
   * @param currentOpenId - 現在開いているビジネスID
   * @param tableType - テーブルの種類（デフォルト: 'virtualized'）
   * @returns CSSクラス名
   */
  const tableNumberStyleClass = (business: IParentBusiness, currentOpenId?: string, tableType: TableType = 'virtualized'): string => {
    const isOpen = currentOpenId === business.businessId;

    if (tableType === 'ez') {
      return isOpen
        ? 'inventory-control__table-item inventory-control__table-item--number-font-weight-bold'
        : 'inventory-control__table-item inventory-control__table-item--number';
    }

    // 仮想テーブル用
    return isOpen ? 'table-cell table-cell--number table-cell--bold' : 'table-cell table-cell--number';
  };

  /**
   * 在庫ステータスのスタイルクラスを返す
   * @param business - ビジネスオブジェクト
   * @param tableType - テーブルの種類（デフォルト: 'virtualized'）
   * @returns CSSクラス名
   */
  const getNowInventoryStatusClass = (business: IParentBusiness, tableType: TableType = 'virtualized'): string => {
    if (business.inventoryConditionAtExecutionDate === null || business.inventoryConditionAtExecutionDate === undefined) {
      return '';
    }

    const baseStatusMap: Record<string, string> = {
      [InventoryCondition.High]: tableType === 'ez' ? 'inventory-control__table-item--high' : 'table-cell--inventory-high',
      [InventoryCondition.Normal]: tableType === 'ez' ? 'inventory-control__table-item--normal' : 'table-cell--normal',
      [InventoryCondition.Low]: tableType === 'ez' ? 'inventory-control__table-item--low' : 'table-cell--inventory-low',
    };

    return baseStatusMap[business.inventoryConditionAtExecutionDate] || '';
  };

  /**
   * 値が負の数かどうかを判定
   * @param value - 判定する値
   * @returns 負の数の場合true
   */
  const isNegative = (value?: number): boolean => {
    return value !== undefined && value < 0;
  };

  /**
   * 行の展開/折りたたみを切り替える
   * @param businessId - ビジネスID
   * @param currentOpenId - 現在開いているID
   * @returns 新しく開いたID、またはundefined
   */
  const handleToggle = (businessId: string, currentOpenId?: string): string | undefined => {
    return currentOpenId === businessId ? undefined : businessId;
  };

  /**
   * スクロールハンドラーを作成する高階関数
   * @param onScrollEnd - スクロール終了時のコールバック
   * @returns スクロールイベントハンドラー
   */
  const createScrollHandler = (onScrollEnd?: () => void) => {
    const SCROLL_END_DELAY = 50; // ミリ秒

    return (event: Event): void => {
      if (scrollTimer.value) {
        clearTimeout(scrollTimer.value);
      }

      if (onScrollEnd) {
        scrollTimer.value = window.setTimeout(() => {
          onScrollEnd();
        }, SCROLL_END_DELAY);
      }
    };
  };

  return {
    tableTextStyleClass,
    tableNumberStyleClass,
    getNowInventoryStatusClass,
    isNegative,
    handleToggle,
    createScrollHandler,
  };
};
